/* Public offers and customer-entered codes use the existing Shopify session cart. */
window.SimpsonHubOffers = class {
  constructor(hub) {
    this.hub = hub;
    this.view = hub.querySelector('[data-hub-view="offers"]');
    if (!this.view) return;
    this.strings = JSON.parse(this.view.querySelector('[data-hub-coupon-strings]').textContent);
    // Liquid translations can encode ampersands before JSON serialization.
    this.strings.copy_apply = this.strings.copy_apply.replace(/&amp;/g, '&');
    this.status = this.view.querySelector('[data-hub-coupon-status]');
    this.listeners = new AbortController();
    this.wallet = this.view.querySelector('[data-hub-coupon-wallet]');
    this.walletRevision = 0;
    this.countBadges = Array.from(hub.querySelectorAll('.customer-hub__layout-shortcut[data-hub-go="offers"]'), card => {
      const badge = document.createElement('span');
      badge.className = 'customer-hub__coupon-count'; badge.hidden = true;
      card.insertBefore(badge, card.lastElementChild);
      return badge;
    });
    this.labels = new Map(Array.from(this.view.querySelectorAll('[data-hub-apply-code]'), button => [button, button.textContent]));
    this.view.addEventListener('click', event => {
      if (event.target.closest('[data-hub-wallet-retry]')) this.loadWallet();
      const button = event.target.closest('[data-hub-apply-code]');
      if (button) this.apply(button.dataset.hubApplyCode, button, true);
    }, { signal: this.listeners.signal });
    hub.addEventListener('click', event => {
      if (event.target.closest('[data-hub-account-sign-out]')) this.clearWallet();
    }, { signal: this.listeners.signal });
    this.view.addEventListener('submit', event => {
      if (!event.target.matches('[data-hub-coupon-form]')) return;
      event.preventDefault();
      this.apply(event.target.elements.discount.value, event.submitter || event.target.querySelector('button'));
    }, { signal: this.listeners.signal });
  }

  destroy() {
    this.listeners?.abort();
    this.request?.abort();
    this.clearWallet();
  }

  clearWallet() {
    ++this.walletRevision;
    this.walletLoading = false;
    this.walletCustomerId = null;
    this.walletLoadedAt = 0;
    this.walletExpiresAt = 0;
    this.setCouponCount(null);
    clearTimeout(this.walletTimer);
    this.walletRequest?.abort();
    if (this.wallet) {
      this.wallet.querySelectorAll('[data-hub-apply-code]').forEach(button => this.labels.delete(button));
      this.wallet.replaceChildren();
    }
  }

  activate(destination) {
    if (this.view.dataset.walletEnabled !== 'true' || !['home','offers'].includes(destination)) return;
    const account = this.hub.account;
    if (!account?.connected() || account.loggingOut) { this.clearWallet(); return; }
    if (this.walletLoading) return;
    if (this.walletCustomerId === account.customerId && this.walletLoadedAt && Date.now() - this.walletLoadedAt < 30000 && (!this.walletExpiresAt || Date.now() < this.walletExpiresAt)) return;
    this.loadWallet();
  }

  setCouponCount(count) {
    (this.countBadges || []).forEach(badge => {
      badge.hidden = !Number.isInteger(count) || count < 1;
      badge.textContent = badge.hidden ? '' : String(count);
      if (badge.hidden) badge.removeAttribute('aria-label');
      else badge.setAttribute('aria-label', `${this.strings.wallet_title}: ${count}`);
    });
  }

  async loadWallet() {
    if (!this.wallet || this.view.dataset.walletEnabled !== 'true') return;
    this.clearWallet();
    const revision = this.walletRevision;
    const account = this.hub.account;
    if (!account?.connected() || account.loggingOut) { this.walletMessage(this.strings.wallet_sign_in); return; }
    const customerId = account.customerId;
    this.walletLoading = true;
    let diagnostic = 'token_refresh';
    this.view.dataset.walletDiagnostic = diagnostic;
    this.walletMessage(this.strings.wallet_loading);
    this.walletRequest = new AbortController();
    this.walletTimer = setTimeout(() => {
      if (revision !== this.walletRevision || this.listeners.signal.aborted) return;
      this.clearWallet(); this.walletMessage(this.strings.wallet_error,true);
      this.walletDiagnostic('request_timeout');
    }, 30000);
    try {
      const access = await account.access();
      if (revision !== this.walletRevision || account.loggingOut) return;
      diagnostic = 'network_or_cors';
      // The fixed Worker verifies this token against our Shopify store before deriving ownership.
      const response = await fetch('https://simpson-hub-api.grant-286.workers.dev/coupons', {
        method:'POST',
        headers: {Authorization:`Bearer ${access}`, Accept:'application/json'},
        credentials:'omit', cache:'no-store', redirect:'error',
        signal:AbortSignal.any([this.walletRequest.signal, AbortSignal.timeout(30000)])
      });
      if (response.status === 401 && revision === this.walletRevision && customerId === account.customerId) {
        account.clearSession(); account.controls(); this.walletMessage(this.strings.wallet_sign_in); return;
      }
      if (!response.ok) {
        let failure;
        try { failure = await response.json(); } catch {}
        const safeFailures = new Set(['configuration_required','too_many_requests','sign_in_required','customer_unavailable','customer_format','customer_query','shopify_auth_unavailable','shopify_auth_busy','shopify_auth_format','shopify_unavailable','shopify_busy','shopify_format','shopify_query','shopify_incomplete','shopify_code_lookup','klaviyo_unavailable','klaviyo_busy','klaviyo_format','klaviyo_incomplete','klaviyo_pagination','klaviyo_profile','klaviyo_metric_missing','klaviyo_event_format','source_format','wallet_limit','service_unavailable']);
        diagnostic = safeFailures.has(failure?.error) || /^klaviyo_profile_(ambiguous|format|email)$/.test(failure?.error || '') || /^(customer|klaviyo)_transport$/.test(failure?.error || '') || /^(customer|klaviyo|shopify_auth)_http_[345]\d{2}$/.test(failure?.error || '') ? failure.error : 'response_error';
        throw new Error('wallet');
      }
      diagnostic = 'response_format';
      const body = await response.json();
      if (revision !== this.walletRevision || !account.connected() || account.loggingOut || customerId !== account.customerId) return;
      if (body.customerId !== `gid://shopify/Customer/${customerId}` || body.complete !== true || !Array.isArray(body.coupons) || body.coupons.length > 100) throw new Error('wallet');
      const coupons = body.coupons.map(item => this.walletCoupon(item));
      if (coupons.some(item => !item)) throw new Error('wallet');
      this.walletCustomerId = customerId;
      this.walletLoadedAt = Date.now();
      this.walletExpiresAt = coupons.reduce((earliest,item) => item.expiresAt ? Math.min(earliest,Date.parse(item.expiresAt)) : earliest, Infinity);
      this.setCouponCount(new Set(coupons.map(item => item.code.toUpperCase())).size);
      this.wallet.replaceChildren();
      if (!coupons.length) this.walletMessage(this.strings.wallet_empty);
      coupons.forEach(item => {
        const card = document.createElement('article'); card.className = 'customer-hub__profile-card customer-hub__coupon';
        const title = document.createElement('h4'); title.textContent = item.title; card.append(title);
        if (item.conditions) { const terms = document.createElement('p'); terms.className='customer-hub__muted'; terms.textContent=item.conditions; card.append(terms); }
        if (item.expiresAt) { const expiry = document.createElement('p'); expiry.textContent = `${this.strings.wallet_expires} ${new Intl.DateTimeFormat(document.documentElement.lang || 'en',{dateStyle:'medium',timeStyle:'short'}).format(new Date(item.expiresAt))}`; card.append(expiry); }
        const code = document.createElement('p'); code.className='customer-hub__coupon-code'; code.textContent=item.code; card.append(code);
        const button = document.createElement('button'); button.type='button'; button.className='customer-hub__button customer-hub__button--secondary'; button.dataset.hubApplyCode=item.code; button.textContent=this.strings.copy_apply;
        this.labels.set(button,button.textContent); card.append(button); this.wallet.append(card);
      });
      this.view.dataset.walletDiagnostic = 'success';
      if (this.cart) this.syncCart(this.cart);
    } catch {
      if (revision !== this.walletRevision || this.listeners.signal.aborted) return;
      this.clearWallet(); this.walletMessage(this.strings.wallet_error,true);
      this.walletDiagnostic(diagnostic);
    } finally {
      if (revision === this.walletRevision) { clearTimeout(this.walletTimer); this.walletLoading = false; }
    }
  }

  walletDiagnostic(code) {
    // Only controlled category names; never include upstream text, tokens, emails or coupon codes.
    this.view.dataset.walletDiagnostic = code;
    if (new URLSearchParams(location.search).get('hub_coupon_debug') === '1') {
      const note = document.createElement('p'); note.textContent = `Connection check: ${code}`; this.wallet.append(note);
    }
  }

  walletCoupon(item) {
    if (!item || typeof item.code !== 'string' || !item.code.trim() || item.code.length > 255 || /[,\r\n]/.test(item.code) || typeof item.title !== 'string' || !item.title.trim() || item.title.length > 200 || typeof item.conditions !== 'string' || item.conditions.length > 2000 || item.status !== 'active') return null;
    if (item.expiresAt != null && (typeof item.expiresAt !== 'string' || !Number.isFinite(Date.parse(item.expiresAt)) || Date.parse(item.expiresAt) <= Date.now())) return null;
    return {code:item.code.trim(),title:item.title,conditions:item.conditions,expiresAt:item.expiresAt};
  }

  walletMessage(text, retry = false) {
    const message = document.createElement('p'); message.textContent = text; message.setAttribute('role','status'); this.wallet.replaceChildren(message);
    if (retry) { const button=document.createElement('button'); button.type='button'; button.dataset.hubWalletRetry=''; button.className='customer-hub__button customer-hub__button--secondary'; button.textContent=this.strings.wallet_retry; this.wallet.append(button); }
  }

  syncCart(cart) {
    this.cart = cart;
    for (const [button, label] of this.labels || []) {
      const applied = cart.discount_codes?.some(item => item.applicable === true && this.sameCode(item.code, button.dataset.hubApplyCode));
      button.disabled = this.busy || Boolean(applied);
      button.textContent = applied ? this.strings.applied : label;
    }
    if (!this.busy && this.hub.drawer?.open && this.hub.currentView === 'offers' && !this.hub.drawer.contains(document.activeElement)) this.restoreFocus();
  }

  restoreFocus(trigger) {
    if (this.hub.isConnected && this.hub.drawer?.open && this.hub.currentView === 'offers' && window.FoxTheme?.a11y?.trapFocus) {
      const focusTarget = trigger && !trigger.disabled ? trigger : this.view.querySelector('input');
      FoxTheme.a11y.trapFocus(this.hub.drawer, focusTarget);
    }
  }

  sameCode(a, b) { return typeof a === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase(); }

  async getCart(signal) {
    const response = await fetch(this.hub.dataset.cartUrl, { credentials: 'same-origin', cache: 'no-store', signal });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }

  async apply(rawCode, trigger, copy = false) {
    if (this.busy) return;
    const code = rawCode.trim();
    if (!code || code.length > 255 || code.includes(',') || /[\r\n]/.test(code)) {
      this.status.textContent = this.strings.invalid_entry;
      return;
    }
    this.busy = true;
    // Start clipboard access inside the click gesture, before any cart requests.
    const clipboard = copy ? this.copyCode(code) : Promise.resolve(null);
    let copyResult = null;
    const report = message => {
      this.status.textContent = copyResult === true ? `${this.strings.copied} ${message}` : copyResult === false ? `${message} ${this.strings.copy_failed}` : message;
    };
    const controls = Array.from(this.view.querySelectorAll('button, input'));
    const previousDisabled = controls.map(control => control.disabled);
    controls.forEach(control => { control.disabled = true; });
    trigger?.setAttribute('aria-busy', 'true');
    this.status.textContent = this.strings.applying;
    this.request = new AbortController();
    const signal = AbortSignal.any([this.request.signal, AbortSignal.timeout(20000)]);
    try {
      copyResult = await clipboard;
      // Read at submission time so previously selected cart codes are retained.
      const before = await this.getCart(signal);
      if (!Number.isFinite(before.item_count)) throw new Error('Invalid cart');
      if (before.item_count === 0) {
        report(this.strings.empty_cart);
        return;
      }
      const existing = Array.isArray(before.discount_codes) ? before.discount_codes : null;
      // Unknown existing codes must not be overwritten with a newly submitted code.
      if (!existing) throw new Error('Discount state unavailable');
      if (existing.some(item => item.applicable === true && this.sameCode(item.code, code))) {
        this.hub.renderCart(before);
        report(this.strings.applied);
        return;
      }
      const codes = existing.filter(item => typeof item.code === 'string').map(item => item.code);
      if (!codes.some(item => this.sameCode(item, code))) codes.push(code);
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
      const response = await fetch(this.hub.dataset.cartUpdateUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: codes.join(','), sections: [...new Set(sections)].slice(0, 5), sections_url: location.pathname }), signal
      });
      if (!response.ok) throw new Error('Discount update failed');
      const updated = await response.json();
      // A second cart read confirms the current result; a successful POST alone is insufficient.
      const cart = await this.getCart(signal);
      if (!this.hub.isConnected || this.listeners.signal.aborted) return;
      cart.sections = updated.sections;
      this.hub.cartRequest?.abort();
      this.hub.cartRevision++;
      this.hub.renderCart(cart);
      if (window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      const applied = cart.discount_codes?.some(item => item.applicable === true && this.sameCode(item.code, code));
      const displaced = applied && existing.some(item => item.applicable === true && !cart.discount_codes?.some(now => now.applicable === true && this.sameCode(now.code, item.code)));
      report(!Array.isArray(cart.discount_codes) ? this.strings.uncertain : displaced ? this.strings.replaced : applied ? this.strings.applied : this.strings.invalid);
    } catch {
      if (!this.listeners.signal.aborted) {
        report(this.strings.uncertain);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } finally {
      this.busy = false;
      controls.forEach((control, index) => { control.disabled = previousDisabled[index]; });
      if (this.cart) this.syncCart(this.cart);
      trigger?.removeAttribute('aria-busy');
      // Pillar cart subscribers also set a focus trap, even with their drawer closed.
      // Keep the currently open hub in control after a background cart refresh.
      this.restoreFocus(trigger);
    }
  }

  async copyCode(code) {
    try {
      if (!navigator.clipboard?.writeText) return false;
      await navigator.clipboard.writeText(code);
      return true;
    } catch { return false; }
  }
};
